import { FormControl, Grid, InputLabel, ListSubheader, MenuItem, Select, SelectChangeEvent, Tooltip } from "@mui/material";
import { memo, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { RootState, useAppSelector } from "../../store/store";
import ShareIcon from "@mui/icons-material/Share";

interface PredictionModelSelectionProps {
    predictionDataId: string | null
}

const PredictionModelSelection = ({ predictionDataId }: PredictionModelSelectionProps) => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    
    const { predictionModels } = useAppSelector((state: RootState) => state.prediction);

    const groupedModels = useMemo(() => {
        return predictionModels.reduce((acc: any, model: any) => {
            const { trainingDatasetId, trainingDatasetName } = model;
            acc[trainingDatasetId] = acc[trainingDatasetId] || {
                trainingDatasetName,
                models: [],
            };
            acc[trainingDatasetId].models.push(model);
            return acc;
        }, {});
    }, [predictionModels]);

    const handleModelChange = useCallback(
        (event: SelectChangeEvent) => {
            const modelId = event.target.value;
            const modelName = predictionModels.find((model: any) => model.id === modelId)?.name;
            if(modelId && modelName) navigate(`?id=${modelId}&name=${modelName}`);
        },
        [predictionModels, navigate]
    );

    return(
        <Grid item pb={1}>
            <FormControl sx={{ mb: 1, mt: 1, minWidth: 300 }} size="small">
                <InputLabel id="demo-select-small-label">
                    {t("PREDICTION_MODELS_SELECT_LABEL")}
                </InputLabel>
                <Select
                    labelId="demo-select-small-label"
                    id="demo-select-small"
                    value={predictionDataId ?? ""}
                    label={t("PREDICTION_MODELS_SELECT_LABEL")}
                    onChange={handleModelChange}
                >
                    {Object.keys(groupedModels).map((trainingDatasetId, index) => {
                        const group = groupedModels[trainingDatasetId];
                        return [
                            <ListSubheader key={index} className='button-text-color' sx={{ fontWeight: 'bold' }}>
                                {group.trainingDatasetName}
                            </ListSubheader>,
                            ...group.models.map((model: any) => (
                                <MenuItem key={model.id} value={model.id} sx={{ pl: 3.5 }}>
                                    {model.name}{" "}
                                    {model.isShared && (
                                    <Tooltip title={t('PREDICTION_SHARED_MODEL') + model.sharedBy}>
                                        <ShareIcon 
                                            className="info-icon"
                                            sx={{
                                                ml: 1,
                                                fontSize: 18,
                                                cursor: "pointer"
                                            }} 
                                        />
                                    </Tooltip>
                                )}
                                </MenuItem>
                            )),
                        ];
                    })}
                </Select>
            </FormControl>
        </Grid>
    )
}

export default memo(PredictionModelSelection);